import React, { useState, useEffect } from 'react';
import { SlidersHorizontal, X } from 'lucide-react';

/* Filters that stay inline on tablets and desktops, and sit behind one "Filters" button on phones, where they open in a
   bottom sheet like in the mobile app. The search box is left outside this wrapper so it stays visible on the page.
   On wide screens the wrapper is invisible to the layout (display: contents), so the page's own grid or flex row is unchanged.

   Usage: <PhoneFilters count={number of filters in use}> ...the filter controls... </PhoneFilters> */
export default function PhoneFilters({ children, count = 0, title = 'Filters', onClear }) {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  return (
    <>
      <div className="hidden sm:contents">{children}</div>

      <button
        type="button"
        onClick={() => setOpen(true)}
        className="sm:hidden col-span-full mb-3 inline-flex items-center justify-center gap-2 w-full px-3 py-2 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100"
        aria-haspopup="dialog"
      >
        <SlidersHorizontal size={16} />
        {title}
        {count > 0 && <span className="px-2 py-0.5 text-xs font-semibold text-white bg-blue-600 rounded-full">{count}</span>}
      </button>

      {open && (
        <div className="sm:hidden fixed inset-0 z-50 flex items-end bg-black bg-opacity-50" onClick={() => setOpen(false)} role="presentation">
          <div
            role="dialog"
            aria-label={title}
            className="w-full max-h-[85vh] overflow-y-auto rounded-t-2xl bg-white dark:bg-slate-800 p-4 pb-6 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-300 dark:bg-slate-600" />
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-base font-semibold text-gray-900 dark:text-slate-100">{title}</h3>
              <button type="button" onClick={() => setOpen(false)} className="p-1 text-gray-500 rounded hover:bg-gray-100 dark:hover:bg-slate-700" aria-label="Close filters">
                <X size={20} />
              </button>
            </div>
            <div className="phone-filter-sheet space-y-4 pb-32">{children}</div>
            <div className="sticky bottom-0 flex gap-2 pt-3 bg-white dark:bg-slate-800">
              {onClear && (
                <button type="button" onClick={onClear} className="flex-1 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg dark:bg-slate-700 dark:text-slate-100">Clear</button>
              )}
              <button type="button" onClick={() => setOpen(false)} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Show results</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
